// React hook wrapping `subscribePresence` from the Matrix wrapper.
// Consumers see a stable `{ state, lastActive }` snapshot that updates
// on every presence change; all SDK mechanics stay behind this seam.
//
// Identical contract to `useTimeline`: the subscription is attached
// inside a `useEffect`, first render sees the default (`offline`, no
// lastActive), and the subscription's synchronous initial callback
// swaps in the real snapshot on the next render. Subscriptions are
// the idiomatic `useEffect` case — not a data fetch.
//
// Passing a `null` userId detaches — useful when the room header
// renders a channel (not a DM) and wants to skip the subscription.

import { useEffect, useState } from "react";

import { subscribePresence, type Client, type PresenceSnapshot } from "~/lib/matrix";

const DEFAULT_SNAPSHOT: PresenceSnapshot = { state: "offline", lastActive: null };

/**
 * Subscribe to a single user's presence. Returns the current snapshot
 * and re-renders on every state change.
 */
export function usePresence(client: Client | null, userId: string | null): PresenceSnapshot {
  const [snapshot, setSnapshot] = useState<PresenceSnapshot>(DEFAULT_SNAPSHOT);

  useEffect(() => {
    if (!client || !userId) {
      setSnapshot(DEFAULT_SNAPSHOT);
      return;
    }
    const unsub = subscribePresence(client, userId, (next) => {
      setSnapshot(next);
    });
    return () => {
      unsub();
    };
  }, [client, userId]);

  return snapshot;
}
